<template>
  <div class="grid text-sm text-gray-800" :class="{ 'md:grid-cols-2': nextToMap }" :data-test="`storecard-${name}`">
    <div class="grid">
      <slot name="above-map" />
      <GoogleMap
        v-if="isMapVisible && !staticMap && location"
        class="pb-6"
        :locations="storeLocation"
        :class="mapHeightClass"
      />
      <NuxtLink v-if="isMapVisible && staticMap && location" :to="'/butiker/' + attrs.id">
        <StaticMap :location="location" />
      </NuxtLink>
    </div>
    <div class="flex flex-col" :class="{ 'px-4 py-6 lg:p-6': paddedAddress, 'pt-6': isMapVisible }">
      <div class="grid w-full grid-cols-2 grid-row-flow gap-y-4">
        <div class="col-span-2">
          <slot name="above-store-info">
            <div class="flex items-center gap-x-2">
              <CHeading v-if="props.title" :level="1" class="xs:text-base">
                {{ props.title }}
              </CHeading>
              <span v-if="attrs.label" class="text-white bg-primary-900 px-1 py-[2px] rounded">{{ attrs.label }}</span>
            </div>
          </slot>
        </div>
        <div class="flex flex-col col-span-2 row-span-2 gap-4" :class="{ '2xl:col-auto': renderTwoColumns }">
          <div v-if="attrs.address">
            <address>{{ attrs.address }}</address>
          </div>
          <div v-if="size === 'large'">
            <CLink
              :href="`https://www.google.com/maps/dir/?api=1&destination=${props.title}+${attrs.address}`"
              target="_blank"
              rel="noopener noreferrer"
              variant="dark"
              size="small"
              icon="location_on"
              icon-position="right"
            >
              {{ t('store.find_location') }}
            </CLink>
          </div>
          <div v-if="attrs.phone && size === 'large'" class="flex gap-3">
            <span class="font-bold">{{ t('telephone') }}: </span>
            <NuxtLink :to="'tel:' + attrs.phone">{{ attrs.phone }}</NuxtLink>
          </div>
          <div v-if="attrs.email && size === 'large'" class="flex gap-3">
            <span class="font-bold">{{ t('email') }}: </span>
            <NuxtLink :href="'mailto:' + attrs.email" class="underline">{{ attrs.email }}</NuxtLink>
          </div>
          <StoreOpeningHours
            v-if="hasOpeningHours"
            :opening-hours="openingHours"
            :todays-opening-hours-only="size === 'small'"
            :more-opening-hours-on-store-page="size === 'small' && moreOpeningHoursOnStorePage"
          />
          <slot name="below-store-info" />
        </div>
        <div
          v-if="phoneNumbers.length"
          class="grid grid-cols-1 col-span-2 row-span-2 gap-2 pt-6 xl:grid-cols-2"
          :class="{
            '2xl:col-auto 2xl:pt-0': renderTwoColumns,
          }"
        >
          <div v-for="(phone, index) in phoneNumbers" :key="index" class="flex flex-row xl:flex-col">
            <span class="font-bold truncate" :title="getPhoneNumber(phone).role">
              {{ getPhoneNumber(phone).role }}:
            </span>
            <NuxtLink :href="`tel:${getPhoneNumber(phone).phone}`" class="pl-2 xl:pl-0">
              {{ getPhoneNumber(phone).phone }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

type Props = {
  location?: {
    lat: number;
    lon: number;
  };
  paddedAddress?: boolean;
  title?: string;
  isMapVisible?: boolean;
  staticMap?: boolean;
  mapHeight?: string;
  name?: string;
  renderTwoColumns?: boolean;
  nextToMap?: boolean;
  size?: 'small' | 'large';
  showOnMapButton?: boolean;
  moreOpeningHoursOnStorePage?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  location: undefined,
  paddedAddress: true,
  isMapVisible: true,
  staticMap: false,
  mapHeight: 'large',
  name: '',
  renderTwoColumns: false,
  nextToMap: false,
  size: 'large',
  showOnMapButton: false,
  moreOpeningHoursOnStorePage: false,
});

const { t } = useI18n();
const attrs = useAttrs();

const mapHeightClass = computed(() => {
  return {
    'h-60': props.mapHeight === 'large',
    'h-40': props.mapHeight === 'small',
    'h-60 md:h-full': props.mapHeight === 'full',
  };
});

const storeLocation = computed(() => {
  if (props.location?.lat && props.location?.lon) {
    return [
      {
        lat: props.location?.lat,
        lng: props.location?.lon,
        text: props.name,
      },
    ];
  } else {
    return [];
  }
});

const openingHours = computed(() => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const obj: { [key: string]: string } = {};
  days.forEach((day: string) => {
    if (attrs[`openingHours${day}s`]) {
      obj[day] = attrs[`openingHours${day}s`] as string;
    }
  });
  return obj;
});

const hasOpeningHours = computed(() => {
  return Object.keys(openingHours.value).length > 0;
});

const phoneNumbers = computed(() => {
  return attrs.phoneNumbers || (attrs.phoneNumbersCollection as any)?.items || [];
});

const getPhoneNumber = (phone: any) => {
  return {
    role: phone.fields?.role || phone.role,
    phone: phone.fields?.phone || phone.phone,
  };
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'StoreDetailsCard',
});
</script>
